/**
 * One round of a board, as the kit runs it: the game clock and loop, the feedback every board
 * gives the same way (a glow for a move that counts, a shake for a mistake), the clear after a
 * win on the game clock (so an alarm over the board holds it too), and the view handed to the
 * mirror.
 */
import { useEffect, useRef } from 'react';
import type { RoundProps } from './types.js';
import { useTicker } from './clock.js';

/** A won round shows its clear for this long, on the game clock, before the station hears of it. */
export const CLEAR_MS = 700;
/** A hit's glow fades over this. */
export const HIT_MS = 450;
/** A mistake's shake and red edge fade over this. */
export const MISS_MS = 500;

/**
 * The feedback as it stands, for a view state: the glow and the shake, each 1 at its moment and
 * fading to 0. Left out of a view (undefined) while both are still.
 */
export type Fx = readonly [hit: number, miss: number];

export interface Round {
  /** The game clock, ms, for stamping a touch. */
  time: () => number;
  /** The game clock as this render draws it. */
  now: number;
  /** A touch counts now: nothing covers the board and the round is not yet won. */
  live: boolean;
  /** The round is won and its clear is showing. */
  won: boolean;
  /** How far through the clear, 0 to 1 (0 until the round is won). */
  clear: number;
  /** A move that counts: the board glows. */
  hit: () => void;
  /** A wrong move: the board shakes and reddens, and the station counts it. */
  miss: () => void;
  /** How far through the round, 0 to 1. */
  progress: (fraction: number) => void;
  /** The round is won: a glow, the progress full, and the station hears it once the clear is over. Once. */
  win: () => void;
  /** The glow and the shake as they stand, for the view state. */
  fx: Fx | undefined;
  /** Hands the view to the mirror (only while it is on) and returns it: `<View view={round.show(view)} />`. */
  show: <V>(view: V) => V;
}

/**
 * The round's loop and feedback. `step(dt, now, won)` runs once a frame while nothing covers the
 * board — `dt` in seconds, `now` on the game clock, `won` once the clear has begun (for an outro) —
 * and the board redraws every frame.
 */
export function useRound(props: RoundProps, step?: (dt: number, now: number, won: boolean) => void): Round {
  const cbs = useRef(props);
  cbs.current = props;
  const hitAt = useRef(-1e9);
  const missAt = useRef(-1e9);
  const wonAt = useRef<number | null>(null);
  const cleared = useRef(false);
  const sent = useRef(-1);
  const latest = useRef<unknown>(undefined);

  const time = useTicker(props.paused, (dt, now) => {
    step?.(dt, now, wonAt.current !== null);
    if (wonAt.current !== null && !cleared.current && now >= wonAt.current + CLEAR_MS) {
      cleared.current = true;
      cbs.current.onCleared();
    }
  });

  // The mirror gets the view drawn, after the frame is on screen (and only while it is on).
  useEffect(() => {
    if (latest.current !== undefined) cbs.current.onView?.(latest.current);
  });

  const now = time();
  const won = wonAt.current !== null;
  const progress = (fraction: number) => {
    const f = Math.round(Math.min(1, Math.max(0, fraction)) * 100) / 100;
    if (f === sent.current) return;
    sent.current = f;
    cbs.current.onProgress(f);
  };
  const h = Math.max(0, 1 - (now - hitAt.current) / HIT_MS);
  const m = Math.max(0, 1 - (now - missAt.current) / MISS_MS);
  return {
    time,
    now,
    live: !props.paused && !won,
    won,
    clear: won ? Math.min(1, (now - wonAt.current!) / CLEAR_MS) : 0,
    hit: () => {
      hitAt.current = time();
    },
    miss: () => {
      if (wonAt.current !== null) return;
      missAt.current = time();
      cbs.current.onMistake();
    },
    progress,
    win: () => {
      if (wonAt.current !== null) return;
      wonAt.current = time();
      hitAt.current = wonAt.current;
      progress(1);
    },
    fx: h > 0 || m > 0 ? [Math.round(h * 100) / 100, Math.round(m * 100) / 100] : undefined,
    show: <V,>(view: V): V => {
      latest.current = view;
      return view;
    },
  };
}
